import { useState, useEffect, useCallback } from "react";
import { cn } from "@/lib/utils";
import { APP_VERSION } from "@/lib/constants";
import type { ErrorInfo } from "react";

interface ErrorDetailsViewProps {
  error: Error;
  errorInfo?: ErrorInfo | null | undefined;
  route: string;
  onRetry: () => void;
  onReport: () => void;
  onBack: () => void;
}

export function ErrorDetailsView({
  error,
  errorInfo,
  route,
  onRetry,
  onReport,
  onBack,
}: ErrorDetailsViewProps) {
  const [copied, setCopied] = useState(false);
  const stackTrace = errorInfo?.componentStack ?? error.stack ?? "No stack trace available";

  const buildReport = useCallback(() => [
    `Error: ${error.name} — ${error.message}`,
    `Route: ${route}`,
    `Timestamp: ${new Date().toISOString()}`,
    `User Agent: ${navigator.userAgent}`,
    "",
    "--- Stack Trace ---",
    stackTrace,
  ].join("\n"), [error, route, stackTrace]);

  const handleCopy = useCallback(() => {
    void navigator.clipboard.writeText(buildReport()).then(() => {
      setCopied(true);
      setTimeout(() => { setCopied(false); }, 2000);
    }).catch(() => { /* clipboard unavailable */ });
  }, [buildReport]);

  // Keyboard shortcuts: 1-4 to select options
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement).tagName;
      if (tag === "INPUT" || tag === "TEXTAREA") return;
      switch (e.key) {
        case "1": e.preventDefault(); onRetry(); break;
        case "2": e.preventDefault(); handleCopy(); break;
        case "3": e.preventDefault(); onReport(); break;
        case "4": e.preventDefault(); onBack(); break;
      }
    };
    window.addEventListener("keydown", handler);
    return () => { window.removeEventListener("keydown", handler); };
  }, [onRetry, handleCopy, onReport, onBack]);

  const stackLines = stackTrace
    .split("\n")
    .filter((l) => l.trim().length > 0)
    .slice(0, 5);

  return (
    <div className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-black">
      <div className="w-full max-w-[700px] px-4 md:px-0">
        <div className="rounded-xl border border-[#3a3a3a] bg-[#1c1c1c] shadow-[0_16px_60px_rgba(0,0,0,0.7),0_0_120px_10px_rgba(51,230,102,0.06)] overflow-hidden">
          {/* Title bar */}
          <div className="flex items-center h-12 px-5 bg-[#252525]">
            <div className="flex gap-2">
              <div className="w-[13px] h-[13px] rounded-full bg-[#ff5f57]" />
              <div className="w-[13px] h-[13px] rounded-full bg-[#febc2e]" />
              <div className="w-[13px] h-[13px] rounded-full bg-[#28c840]" />
            </div>
            <div className="flex-1 text-center">
              <span className="text-[13px] text-[#6b6b6b] font-mono">
                compbox — error details
              </span>
            </div>
            <div className="w-[54px]" />
          </div>

          <div className="h-px bg-[#333]" />

          {/* Terminal body */}
          <div className="bg-[#111] p-4 md:p-7 font-mono overflow-y-auto max-h-[calc(100vh-200px)]">
            {/* Command line */}
            <div className="flex items-center">
              <span className="text-[14px] font-bold text-green-400">$ </span>
              <span className="text-[14px] font-medium text-[#e6e6e6]">compbox run </span>
              <span className="text-[14px] text-[#9ca3b0]">--check --verbose</span>
            </div>

            <div className="h-5" />

            {/* Error Details header + copy button */}
            <div className="flex items-center justify-between">
              <div className="flex items-start gap-1">
                <span className="text-[16px] leading-[1.4] text-amber-400">⚠</span>
                <span className="text-[15px] font-bold text-red-400"> Error Details</span>
              </div>
              <button
                onClick={handleCopy}
                className="flex items-center gap-1.5 text-xs font-mono px-2.5 py-1 rounded border border-[#3a3a3a] hover:bg-white/5 transition-colors cursor-pointer"
              >
                {copied ? (
                  <>
                    <span className="text-green-400">✓</span>
                    <span className="text-green-400">Copied</span>
                  </>
                ) : (
                  <>
                    <span className="text-[#888]">📋</span>
                    <span className="text-[#999]">Copy</span>
                  </>
                )}
              </button>
            </div>

            <div className="h-4" />

            {/* Detail rows */}
            <div className="space-y-1.5 text-[13px]">
              <DetailRow label="Type:" value={error.name} valueColor="text-red-400 font-bold" />
              <DetailRow label="Route:" value={route} valueColor="text-cyan-400" />
              <DetailRow label="Message:" value={error.message} valueColor="text-[#e6e6e6]" />
            </div>

            <div className="h-5" />

            {/* Stack Trace */}
            <p className="text-[12px] font-medium text-[#6b6b6b] mb-2">Stack Trace</p>
            <div className="rounded-md bg-[#0a0a0a] border border-[#1e1e1e] p-3 space-y-0.5">
              {stackLines.map((line, i) => (
                <p
                  key={i}
                  className="text-xs text-[#8c8c8c] leading-[1.6]"
                  style={{ opacity: 1 - i * 0.12 }}
                >
                  {line.trim()}
                </p>
              ))}
            </div>

            {/* Divider */}
            <div className="h-6" />
            <div className="h-px bg-[#2b2b2b] w-full" />
            <div className="h-6" />

            {/* Actions */}
            <p className="text-[14px] font-medium text-[#e6e6e6] mb-4">
              What would you like to do?
            </p>

            <div className="space-y-1">
              <DetailOption number="1" color="text-green-400" icon="↻" label="Retry connection" onClick={onRetry} />
              <DetailOption number="2" color="text-cyan-400" icon="📋" label={copied ? "Copied to clipboard!" : "Copy error to clipboard"} onClick={handleCopy} />
              <DetailOption number="3" color="text-violet-400" icon="⚑" label="Report this issue" onClick={onReport} />
              <DetailOption number="4" color="text-amber-400" icon="←" label="Go back" onClick={onBack} />
            </div>

            <div className="h-7" />
            <div className="flex items-center text-[13px]">
              <span className="text-[#9ca3b0]">Press a number to continue </span>
              <span className="text-green-400 animate-terminal-blink">▊</span>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-8 text-[12px] text-[#4b5563] font-sans">
        compbox {APP_VERSION}
      </div>
    </div>
  );
}

function DetailRow({ label, value, valueColor }: { label: string; value: string; valueColor: string }) {
  return (
    <div className="flex items-baseline">
      <span className="text-[#9ca3b0] w-24 shrink-0">  {label}</span>
      <span className={cn("ml-2 break-all", valueColor)}>{value}</span>
    </div>
  );
}

function DetailOption({ number, color, icon, label, onClick }: { number: string; color: string; icon: string; label: string; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className="flex items-center gap-4 rounded-md px-3 py-2 hover:bg-white/5 transition-colors w-full text-left cursor-pointer"
    >
      <span className="flex items-center font-mono text-[14px]">
        <span className="text-[#555]">[</span>
        <span className={cn("font-bold", color)}>{number}</span>
        <span className="text-[#555]">]</span>
      </span>
      <span className="flex items-center gap-1 font-mono text-[14px]">
        <span className="text-[#888]">{icon}  </span>
        <span className="text-[#e5e5e5]">{label}</span>
      </span>
    </button>
  );
}
